import { ROLLEN_ANZEIGE, type KontoInfo, type Rolle, type SchluesselInfo } from "@gui-creator/gemeinsam";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, Download, KeyRound, Plus, Trash2, Upload } from "lucide-react";
import { useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { schluessel, useApiSchluessel, useKonten, useTexturStatus, type Status } from "../api/abfragen.ts";
import { api } from "../api/klient.ts";
import { useDarf, useKonto } from "../huelle/konto.ts";
import { farbschemaSetzen, useFarbschema, type Farbschema } from "../stil/farbschema.ts";
import { Bestaetigung } from "../ui/Dialog.tsx";
import { useHinweis } from "../ui/Hinweise.tsx";
import "./seiten.css";
import "./einstellungen.css";

const datum = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium" });
const datumZeit = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" });

function Bereich({ titel, text, children }: { titel: string; text?: string; children: ReactNode }) {
  const id = useId();
  return (
    <section className="bereich einstellung" aria-labelledby={id}>
      <header className="bereich-kopf">
        <div>
          <h2 id={id}>{titel}</h2>
          {text && <p className="leise">{text}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

async function kopieren(text: string, hinweis: ReturnType<typeof useHinweis>) {
  try {
    await navigator.clipboard.writeText(text);
    hinweis({ text: "In die Zwischenablage kopiert." });
  } catch {
    hinweis({ text: "Kopieren ging nicht; bitte von Hand markieren.", art: "fehler" });
  }
}

const FARBSCHEMATA: [Farbschema, string][] = [
  ["dunkel", "Dunkel"],
  ["hell", "Hell"],
  ["system", "Wie das Gerät"],
];

function Darstellung() {
  const wahl = useFarbschema();
  return (
    <Bereich titel="Darstellung" text="Gilt für diesen Browser auf diesem Gerät. Inventar und Bedrock-Formular bleiben in der Spieloptik.">
      <div>
        <div className="umschalter" role="group" aria-label="Farbschema">
          {FARBSCHEMATA.map(([wert, text]) => (
            <button key={wert} type="button" aria-pressed={wahl === wert} onClick={() => farbschemaSetzen(wert)}>
              {text}
            </button>
          ))}
        </div>
      </div>
    </Bereich>
  );
}

function MeinKonto() {
  const id = useId();
  const konto = useKonto();
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const [alt, setAlt] = useState("");
  const [neu, setNeu] = useState("");
  const [nochmal, setNochmal] = useState("");
  const [fragen, setFragen] = useState(false);
  const fehler = neu && neu.length < 10 ? "Mindestens 10 Zeichen." : nochmal && neu !== nochmal ? "Die beiden Eingaben stimmen nicht überein." : null;

  const aendern = async (e: FormEvent) => {
    e.preventDefault();
    if (fehler || !alt || !neu) return;
    try {
      await api(`/api/konten/${konto.kontoId}/passwort`, { methode: "POST", body: { alt, neu } });
      setAlt("");
      setNeu("");
      setNochmal("");
      hinweis({ text: "Das Passwort ist geändert; andere Geräte sind abgemeldet." });
    } catch (f) {
      hinweis({ text: (f as Error).message, art: "fehler" });
    }
  };

  const ueberallAbmelden = async () => {
    setFragen(false);
    try {
      await api("/api/abmelden-ueberall", { methode: "POST" });
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "status" });
      qc.setQueryData<Status>(schluessel.status, { eingerichtet: true, konto: null });
    } catch (f) {
      hinweis({ text: (f as Error).message, art: "fehler" });
    }
  };

  return (
    <Bereich titel="Mein Konto" text={`${konto.name} · ${ROLLEN_ANZEIGE[konto.rolle]}`}>
      <form className="feldgruppe einstellung-schmal" onSubmit={(e) => void aendern(e)}>
        <h3 className="einstellung-unter">Passwort ändern</h3>
        <div className="feld">
          <label htmlFor={`${id}-a`}>Aktuelles Passwort</label>
          <input id={`${id}-a`} type="password" className="eingabe" autoComplete="current-password" value={alt} onChange={(e) => setAlt(e.target.value)} />
        </div>
        <div className="feld">
          <label htmlFor={`${id}-n`}>Neues Passwort</label>
          <input id={`${id}-n`} type="password" className="eingabe" autoComplete="new-password" value={neu} onChange={(e) => setNeu(e.target.value)} aria-invalid={!!fehler} />
        </div>
        <div className="feld">
          <label htmlFor={`${id}-w`}>Neues Passwort wiederholen</label>
          <input id={`${id}-w`} type="password" className="eingabe" autoComplete="new-password" value={nochmal} onChange={(e) => setNochmal(e.target.value)} aria-invalid={!!fehler} />
          {fehler && <span className="feld-fehler">{fehler}</span>}
        </div>
        <div className="knopfreihe">
          <button type="submit" className="knopf" disabled={!!fehler || !alt || !neu || neu !== nochmal}>
            Passwort ändern
          </button>
        </div>
      </form>
      <div className="einstellung-zeile">
        <p className="leise">Beendet die Anmeldung auf allen Geräten, auch auf diesem.</p>
        <button type="button" className="knopf" onClick={() => setFragen(true)}>
          Auf allen Geräten abmelden
        </button>
      </div>
      <Bestaetigung offen={fragen} titel="Überall abmelden?" bestaetigen="Abmelden" onAbbrechen={() => setFragen(false)} onBestaetigen={() => void ueberallAbmelden()}>
        <p>Alle Sitzungen dieses Kontos enden. Danach ist eine neue Anmeldung nötig.</p>
      </Bestaetigung>
    </Bereich>
  );
}

const WAEHLBAR: Rolle[] = ["admin", "bearbeiter", "leser"];

function Konten() {
  const id = useId();
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const ich = useKonto();
  const konten = useKonten(true);
  const [neu, setNeu] = useState(false);
  const [name, setName] = useState("");
  const [passwort, setPasswort] = useState("");
  const [rolle, setRolle] = useState<Rolle>("bearbeiter");

  const ausfuehren = async (f: () => Promise<unknown>, erfolg?: string) => {
    try {
      await f();
      await qc.invalidateQueries({ queryKey: schluessel.konten });
      if (erfolg) hinweis({ text: erfolg });
      return true;
    } catch (e) {
      hinweis({ text: (e as Error).message, art: "fehler" });
      return false;
    }
  };

  const anlegen = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await ausfuehren(() => api("/api/konten", { methode: "POST", body: { name: name.trim(), passwort, rolle } }), `Konto „${name.trim()}“ ist angelegt.`);
    if (ok) {
      setNeu(false);
      setName("");
      setPasswort("");
    }
  };

  const menschen = (konten.data ?? []).filter((k) => !k.istClaude);
  return (
    <Bereich titel="Konten" text="Wer den GUI-Creator nutzen darf und mit welcher Rolle.">
      <table className="tabelle">
        <thead>
          <tr>
            <th>Name</th>
            <th>Rolle</th>
            <th>Aktiv</th>
            <th>Angelegt</th>
          </tr>
        </thead>
        <tbody>
          {menschen.map((k: KontoInfo) => (
            <tr key={k.id} className={k.aktiv ? "" : "zeile-inaktiv"}>
              <td>
                {k.name}
                {k.id === ich.kontoId && <span className="leise"> · angemeldet</span>}
              </td>
              <td>
                {k.rolle === "eigentuemer" ? (
                  ROLLEN_ANZEIGE[k.rolle]
                ) : (
                  <select className="eingabe eingabe-klein" aria-label={`Rolle von ${k.name}`} value={k.rolle} onChange={(e) => void ausfuehren(() => api(`/api/konten/${k.id}`, { methode: "PATCH", body: { rolle: e.target.value } }))}>
                    {WAEHLBAR.map((r) => (
                      <option key={r} value={r}>
                        {ROLLEN_ANZEIGE[r]}
                      </option>
                    ))}
                  </select>
                )}
              </td>
              <td>
                <input
                  type="checkbox"
                  className="notiz-haken"
                  aria-label={`${k.name} aktiv`}
                  checked={k.aktiv}
                  disabled={k.rolle === "eigentuemer"}
                  onChange={(e) => void ausfuehren(() => api(`/api/konten/${k.id}`, { methode: "PATCH", body: { aktiv: e.target.checked } }))}
                />
              </td>
              <td className="zahlen">{datum.format(new Date(k.erstelltAm))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!neu ? (
        <div>
          <button type="button" className="knopf" onClick={() => setNeu(true)}>
            <Plus size={14} aria-hidden="true" /> Konto anlegen
          </button>
        </div>
      ) : (
        <form className="formreihe" onSubmit={(e) => void anlegen(e)}>
          <div className="feld">
            <label htmlFor={`${id}-n`}>Name</label>
            <input id={`${id}-n`} className="eingabe" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" autoFocus />
          </div>
          <div className="feld">
            <label htmlFor={`${id}-p`}>Passwort</label>
            <input id={`${id}-p`} type="password" className="eingabe" value={passwort} onChange={(e) => setPasswort(e.target.value)} autoComplete="new-password" />
          </div>
          <div className="feld">
            <label htmlFor={`${id}-r`}>Rolle</label>
            <select id={`${id}-r`} className="eingabe" value={rolle} onChange={(e) => setRolle(e.target.value as Rolle)}>
              {WAEHLBAR.map((r) => (
                <option key={r} value={r}>
                  {ROLLEN_ANZEIGE[r]}
                </option>
              ))}
            </select>
          </div>
          <div className="knopfreihe">
            <button type="button" className="knopf" onClick={() => setNeu(false)}>
              Abbrechen
            </button>
            <button type="submit" className="knopf knopf-primaer" disabled={name.trim().length < 2 || passwort.length < 10}>
              Anlegen
            </button>
          </div>
        </form>
      )}
    </Bereich>
  );
}

function ApiSchluessel() {
  const id = useId();
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const konten = useKonten(true);
  const liste = useApiSchluessel(true);
  const [bezeichnung, setBezeichnung] = useState("");
  const [neu, setNeu] = useState<string | null>(null);
  const [loeschen, setLoeschen] = useState<SchluesselInfo | null>(null);
  const claude = konten.data?.find((k) => k.istClaude);
  const befehl = neu ? `claude mcp add --transport http gui-creator ${window.location.origin}/mcp --header "Authorization: Bearer ${neu}"` : "";

  const ausfuehren = async <T,>(f: () => Promise<T>): Promise<T | null> => {
    try {
      const r = await f();
      await Promise.all([qc.invalidateQueries({ queryKey: schluessel.konten }), qc.invalidateQueries({ queryKey: schluessel.apiSchluessel })]);
      return r;
    } catch (e) {
      hinweis({ text: (e as Error).message, art: "fehler" });
      return null;
    }
  };

  const erzeugen = async (e: FormEvent) => {
    e.preventDefault();
    if (!claude || !bezeichnung.trim()) return;
    const r = await ausfuehren(() => api<{ id: string; schluessel: string }>("/api/schluessel", { methode: "POST", body: { kontoId: claude.id, bezeichnung: bezeichnung.trim() } }));
    if (r) {
      setNeu(r.schluessel);
      setBezeichnung("");
    }
  };

  return (
    <Bereich titel="Claude und API-Schlüssel" text="Claude arbeitet über MCP mit eigenem Konto und meldet sich nur mit API-Schlüssel an.">
      {konten.isSuccess && !claude && (
        <div className="einstellung-zeile">
          <p className="leise">Noch kein Claude-Konto.</p>
          <button type="button" className="knopf" onClick={() => void ausfuehren(() => api("/api/konten/claude", { methode: "POST" }))}>
            <Plus size={14} aria-hidden="true" /> Claude-Konto anlegen
          </button>
        </div>
      )}
      {claude && (
        <>
          {neu && (
            <div className="schluessel-neu" role="status">
              <div className="feld">
                <label htmlFor={`${id}-k`}>Neuer Schlüssel</label>
                <span className="schluessel-zeile">
                  <input id={`${id}-k`} className="eingabe mono" readOnly value={neu} onFocus={(e) => e.target.select()} />
                  <button type="button" className="knopf" onClick={() => void kopieren(neu, hinweis)}>
                    <Copy size={14} aria-hidden="true" /> Kopieren
                  </button>
                </span>
                <span className="feld-hinweis">Nur jetzt sichtbar. Gespeichert wird allein ein Hash; wer ihn verliert, erzeugt einen neuen.</span>
              </div>
              <div className="feld">
                <span className="feld-titel">Befehl für Claude Code</span>
                <span className="schluessel-zeile">
                  <code className="befehl">{befehl}</code>
                  <button type="button" className="knopf" onClick={() => void kopieren(befehl, hinweis)}>
                    <Copy size={14} aria-hidden="true" /> Kopieren
                  </button>
                </span>
              </div>
              <div>
                <button type="button" className="knopf knopf-still" onClick={() => setNeu(null)}>
                  Fertig
                </button>
              </div>
            </div>
          )}
          {!!liste.data?.length && (
            <table className="tabelle">
              <thead>
                <tr>
                  <th>Bezeichnung</th>
                  <th>Konto</th>
                  <th>Angelegt</th>
                  <th>Zuletzt genutzt</th>
                  <th aria-label="Aktion" />
                </tr>
              </thead>
              <tbody>
                {liste.data.map((s) => (
                  <tr key={s.id}>
                    <td>{s.bezeichnung}</td>
                    <td>{s.kontoName}</td>
                    <td className="zahlen">{datum.format(new Date(s.erstelltAm))}</td>
                    <td className="zahlen">{s.zuletztGenutzt ? datumZeit.format(new Date(s.zuletztGenutzt)) : <span className="leise">nie</span>}</td>
                    <td className="tabelle-aktion">
                      <button type="button" className="knopf knopf-still knopf-symbol" aria-label={`Schlüssel ${s.bezeichnung} löschen`} title="Löschen" onClick={() => setLoeschen(s)}>
                        <Trash2 size={13} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <form className="formreihe" onSubmit={(e) => void erzeugen(e)}>
            <div className="feld">
              <label htmlFor={`${id}-b`}>Bezeichnung</label>
              <input id={`${id}-b`} className="eingabe" value={bezeichnung} placeholder="z. B. Laptop Finn" onChange={(e) => setBezeichnung(e.target.value)} />
            </div>
            <button type="submit" className="knopf" disabled={!bezeichnung.trim()}>
              <KeyRound size={14} aria-hidden="true" /> Schlüssel erzeugen
            </button>
          </form>
        </>
      )}
      <Bestaetigung
        offen={!!loeschen}
        titel="Schlüssel löschen?"
        bestaetigen="Löschen"
        gefahr
        onAbbrechen={() => setLoeschen(null)}
        onBestaetigen={() => {
          const s = loeschen!;
          setLoeschen(null);
          void ausfuehren(() => api(`/api/schluessel/${s.id}`, { methode: "DELETE" }));
        }}
      >
        <p>„{loeschen?.bezeichnung}“ funktioniert danach sofort nicht mehr.</p>
      </Bestaetigung>
    </Bereich>
  );
}

function hochladen(datei: File, fortschritt: (anteil: number) => void): Promise<{ version: string; items: number; deutsch: boolean }> {
  return new Promise((ok, fehler) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/texturen/jar");
    xhr.setRequestHeader("content-type", "application/java-archive");
    xhr.upload.onprogress = (e) => e.lengthComputable && fortschritt(e.loaded / e.total);
    xhr.onload = () => {
      const daten = (() => {
        try {
          return JSON.parse(xhr.responseText);
        } catch {
          return {};
        }
      })();
      if (xhr.status >= 200 && xhr.status < 300) ok(daten);
      else fehler(new Error(daten.meldung ?? "Hochladen fehlgeschlagen."));
    };
    xhr.onerror = () => fehler(new Error("Keine Verbindung zum Server."));
    xhr.send(datei);
  });
}

function Texturen() {
  const id = useId();
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const status = useTexturStatus();
  const eingabe = useRef<HTMLInputElement>(null);
  const [anteil, setAnteil] = useState<number | null>(null);

  const waehlen = async (datei: File | undefined) => {
    if (!datei) return;
    setAnteil(0);
    try {
      const r = await hochladen(datei, setAnteil);
      await qc.invalidateQueries();
      hinweis({ text: `Minecraft ${r.version} eingelesen: ${r.items} Items${r.deutsch ? ", deutsche Namen geladen" : ""}.` });
    } catch (e) {
      hinweis({ text: (e as Error).message, art: "fehler" });
    } finally {
      setAnteil(null);
      if (eingabe.current) eingabe.current.value = "";
    }
  };

  const s = status.data;
  return (
    <Bereich titel="Minecraft-Texturen" text="Item-Bilder und Spielschrift kommen aus der Client-JAR; die deutschen Namen lädt der Server danach von Mojang.">
      <dl className="textur-status">
        <div>
          <dt>Version</dt>
          <dd>{s?.version ?? <span className="leise">keine JAR</span>}</dd>
        </div>
        <div>
          <dt>Items</dt>
          <dd className="zahlen">{s ? s.items.toLocaleString("de-DE") : "–"}</dd>
        </div>
        <div>
          <dt>Deutsche Namen</dt>
          <dd>{s?.deutsch ? "geladen" : <span className="leise">fehlen</span>}</dd>
        </div>
      </dl>
      <div className="einstellung-zeile">
        <input ref={eingabe} id={id} type="file" accept=".jar" className="unsichtbar" onChange={(e) => void waehlen(e.target.files?.[0])} />
        <label htmlFor={id} className={`knopf${anteil !== null ? " knopf-aus" : ""}`} aria-disabled={anteil !== null}>
          <Upload size={14} aria-hidden="true" /> Client-JAR hochladen
        </label>
        {anteil !== null && (
          <span className="hochladen">
            <progress max={1} value={anteil} aria-label="Fortschritt" /> <span className="zahlen">{anteil < 1 ? `${Math.round(anteil * 100)} %` : "wird eingelesen …"}</span>
          </span>
        )}
      </div>
    </Bereich>
  );
}

export function Einstellungen() {
  const konten = useDarf("konten");
  const texturen = useDarf("texturen");
  return (
    <main className="seite">
      <header className="seite-kopf">
        <h1>Einstellungen</h1>
      </header>
      <MeinKonto />
      <Darstellung />
      {konten && <Konten />}
      {konten && <ApiSchluessel />}
      {texturen && <Texturen />}
      {konten && (
        <Bereich titel="Sicherung" text="Der Server sichert täglich selbst. Der Gesamtexport enthält Plugins mit Katalogen, UIs, Stände, Notizen und die Kontenliste ohne Passwörter als JSON.">
          <div>
            <a className="knopf" href="/api/sicherung/export" download>
              <Download size={14} aria-hidden="true" /> Gesamtexport herunterladen
            </a>
          </div>
        </Bereich>
      )}
    </main>
  );
}
